/**
 * Collapse the org chart's card tree into a tree of JOB POSITIONS.
 *
 * The interactive chart renders one card per person plus one per unfilled seat,
 * so a job held by twelve engineers is twelve cards side by side. That is right
 * on screen, where the reader is looking for people, and wrong for a structural
 * export, where those twelve identical cards say one thing twelve times and push
 * the rest of the org off the page.
 *
 * This keeps one node per `jobId` and drops names entirely. On live data that
 * takes roughly 450 cards down to about 100 positions, which is what makes a
 * single-page fit realistic.
 *
 * The node objects are NOT the chart's nodes: they carry only what the positions
 * sheet renders (title, department, id, children). Nothing here reads a person's
 * name, status or photo, so no employment data can leak into this export by
 * accident.
 */

/**
 * The job id off a chart card, accepting either spelling.
 *
 * The API serialises this as `job_id` (snake_case, like every other multi-word
 * key on the wire), while the server's own buildNodes output calls it `jobId`.
 * This module originally read only `jobId`, so on real browser data every lookup
 * missed, every card fell through to its own `node:<id>` key, and the sheet
 * printed one box per PERSON: 187 boxes with "Software Engineer" forty times
 * over, which is the export bug this accessor fixes.
 *
 * Reading both is deliberate rather than a tidier rename on one side. The tests
 * and the server fixtures speak camelCase, the wire speaks snake_case, and a
 * fold that silently degrades into "no folding at all" is a bad failure mode to
 * leave one rename away.
 */
export function jobIdOf(node) {
  const v = node.job_id != null ? node.job_id : node.jobId;
  return v == null ? null : v;
}

/**
 * @param {Array} roots the chart's root nodes, each with a `children` array
 * @returns {Array} root position nodes, each `{ id, title, func, children }`
 */
export function toPositionTree(roots) {
  // jobId -> position node, so several cards for one job fold into one.
  const byJob = new Map();

  /**
   * A card without a jobId cannot be folded by job, so it keeps its own identity
   * and its own node. Live data always sets jobId, but a node built from a job
   * Odoo has since deleted would not have one, and dropping it silently would
   * lose a branch of the tree.
   */
  const keyOf = (node) => {
    const jobId = jobIdOf(node);
    return jobId != null ? `job:${jobId}` : `node:${node.id}`;
  };

  const visit = (node, parentKey) => {
    const key = keyOf(node);
    let pos = byJob.get(key);
    if (!pos) {
      pos = {
        id: key,
        // The job's own name. `title` on a chart card is the job title, and a
        // seat card and a person card holding the same job carry the same one.
        title: node.title || node.name || 'Untitled position',
        func: node.func || null,
        parentKey: parentKey ?? null,
        childKeys: new Set(),
      };
      byJob.set(key, pos);
    } else if (pos.parentKey == null && parentKey != null) {
      // Several cards of one job can sit under different parents when the job is
      // held by people reporting into different places. The first non-null parent
      // wins, which matches the chart's own attach point (representativeFor picks
      // the lowest-id holder).
      pos.parentKey = parentKey;
    }

    for (const child of node.children || []) {
      pos.childKeys.add(keyOf(child));
      visit(child, key);
    }
  };

  for (const r of roots || []) visit(r, null);

  // Materialise children from the key sets, so a job appearing several times in
  // the card tree still has exactly one child list.
  const out = new Map();
  for (const [key, pos] of byJob) {
    out.set(key, { id: pos.id, title: pos.title, func: pos.func, children: [] });
  }
  for (const [key, pos] of byJob) {
    const node = out.get(key);
    for (const childKey of pos.childKeys) {
      const child = out.get(childKey);
      // A self-referencing job would otherwise build a cycle and hang the render.
      if (child && childKey !== key) node.children.push(child);
    }
  }

  // A root is a position that nothing else lists as a child. Derived from the
  // materialised child lists rather than from parentKey, because a job whose only
  // parent link was a self-reference has a non-null parentKey but no real parent,
  // and keying off parentKey alone dropped it from the tree entirely.
  const claimed = new Set();
  for (const pos of out.values()) {
    for (const child of pos.children) claimed.add(child.id);
  }
  const roots2 = [];
  for (const [key, node] of out) {
    if (!claimed.has(key)) roots2.push(node);
  }
  mergeSameTitleSiblings(roots2);
  return roots2;
}

/**
 * Collapse SIBLING positions that share a title, in place, depth-first.
 *
 * Odoo carries separate hr.job records for what an org chart reads as one role:
 * under CX Capabilities Director there are two distinct "Software Engineer" jobs
 * and two "Senior Software Engineer"; under Emerging Technologies Capability
 * Manager, two "DevOps Engineer" and two "AI & ML Engineer". Job-folding cannot
 * merge those because the ids genuinely differ, so the sheet listed the same role
 * twice under one manager and read like a mistake.
 *
 * Scoped to SIBLINGS deliberately, and this is the part to preserve if this
 * function is ever revisited. Merging by title globally would fuse jobs that only
 * coincide in name: "Senior Product Manager" exists under both Ventures GM and
 * Products Portfolio Manager, and those are two different roles in two different
 * parts of the business. Collapsing them would graft one manager's subtree onto
 * the other's and assert a reporting line Odoo does not contain - the sheet would
 * be denser and wrong. Two same-named jobs under ONE parent are the same role; two
 * under DIFFERENT parents are not.
 *
 * The surviving node keeps the first sibling's identity and absorbs the others'
 * children, so a merge can never drop a subtree even though on current data every
 * merged group is childless.
 */
function mergeSameTitleSiblings(list, seen = new Set()) {
  const byTitle = new Map();
  const kept = [];
  for (const node of list) {
    const key = node.title;
    const first = byTitle.get(key);
    if (first) {
      // Absorb, skipping children the survivor already lists so a shared child
      // is not double-added.
      const have = new Set(first.children.map((c) => c.id));
      for (const c of node.children) if (!have.has(c.id)) first.children.push(c);
    } else {
      byTitle.set(key, node);
      kept.push(node);
    }
  }
  // Rewrite in place: callers hold this array (it is a parent's `children`, or the
  // roots array returned to the caller).
  list.length = 0;
  list.push(...kept);

  for (const node of kept) {
    // A job appearing under two parents is shared by reference, so guard against
    // walking it - and its cycles - twice.
    if (seen.has(node.id)) continue;
    seen.add(node.id);
    mergeSameTitleSiblings(node.children, seen);
  }
}

/**
 * Total distinct positions in a tree.
 *
 * Counts by id rather than by walking, because a job held under two different
 * parents legitimately appears in both parents' child lists, and a naive walk
 * would count it once per appearance. The number is used in the caption ("112
 * positions") and to choose the scale, so double counting would both misreport
 * and over-shrink.
 */
export function countPositions(roots) {
  const seen = new Set();
  const walk = (list) => {
    for (const p of list) {
      if (seen.has(p.id)) continue;
      seen.add(p.id);
      walk(p.children);
    }
  };
  walk(roots || []);
  return seen.size;
}

/**
 * Depth of the deepest branch, which drives how much vertical room is needed.
 * Tracks the ids on the current path so a shared job cannot be revisited into an
 * infinite descent.
 */
export function positionDepth(roots) {
  const depth = (list, path) => {
    let best = 0;
    for (const p of list) {
      if (path.has(p.id)) continue;
      path.add(p.id);
      best = Math.max(best, 1 + depth(p.children, path));
      path.delete(p.id);
    }
    return best;
  };
  return depth(roots || [], new Set());
}
